import type {DomainCountry} from '@getmoss/moss-analytics'
import {track as trackMossAnalytics} from '@getmoss/moss-analytics'
import type {ImageField, KeyTextField, LinkField} from '@prismicio/client'
import {isFilled} from '@prismicio/client'
import {PrismicNextLink} from '@prismicio/next/pages'
import cn from 'classnames'
import {motion} from 'framer-motion'
import {useLinkResolver} from 'prismicio'
import {EVENTS, track} from 'src/common/helpers'

type LinkItemProps = {
  link: {
    icon: ImageField
    link_url: LinkField
    link_label: KeyTextField
    link_description: KeyTextField
  }
  index: number
  componentIndex: number
  marketingId: string | null
  pageUid: string
  domainCountry: DomainCountry
  isFirst: boolean
}

export const LinkItem = ({
  link,
  index,
  componentIndex,
  marketingId,
  pageUid,
  domainCountry,
  isFirst,
}: LinkItemProps) => {
  const {linkResolver} = useLinkResolver()

  return (
    <motion.li
      key={`link-${componentIndex}-${index}`}
      whileHover="hover"
      className={cn('group/item isolate grow basis-0 max-lg:px-4', {
        'max-lg:pt-3': !isFirst,
      })}
    >
      <PrismicNextLink
        href={linkResolver(link.link_url)}
        {...(link.link_url.link_type === 'Web' && {target: link.link_url.target || '_self'})}
        {...(link.link_url.link_type === 'Document' && link.link_url.lang ? {locale: link.link_url.lang} : {})}
        onClick={() => {
          track({
            event: EVENTS.CLICK,
            dataType: `order-${componentIndex}`,
            location: 'main-nav-drop-down',
            mktgId: `category-${marketingId}-${index}`,
            pageName: pageUid,
          })

          trackMossAnalytics({
            event: 'interaction',
            interaction: {
              element: `link-${index}`,
              elementText: link.link_label || '',
              location: 'nav-bar-dropdown',
              type: 'click',
            },
            page: {
              type: pageUid,
              domainCountry,
            },
          })
        }}
        className="flex items-center gap-3 font-normal"
      >
        {isFilled.image(link.icon) && (
          <div className="flex h-8 min-w-8 items-center justify-center rounded-[4px] border border-black/[10%] bg-moss-white transition-colors duration-300 lg:border-black/[10%] lg:group-hover/item:border-black/[10%] lg:group-hover/item:bg-green-neon">
            <div
              className="size-4 bg-moss-grey-500 lg:group-hover/item:bg-dark-green"
              style={{
                maskImage: `url(${link.icon.url})`,
                maskSize: 'contain',
                maskRepeat: 'no-repeat',
                WebkitMaskImage: `url(${link.icon.url})`,
                WebkitMaskSize: 'contain',
                WebkitMaskRepeat: 'no-repeat',
              }}
            />
          </div>
        )}
        <div className="flex flex-col gap-1">
          <span className="text-[13.5px] leading-[normal] text-black">
            {link.link_label}
            <motion.span
              initial={{opacity: 0}}
              variants={{
                hover: {opacity: 1},
              }}
              transition={{
                duration: 0.3,
              }}
              className="pl-2 text-[12px]"
            >
              →
            </motion.span>
          </span>
          {isFilled.keyText(link.link_description) && (
            <span className="text-[13.5px] leading-[normal] tracking-[-.13px] text-black/[70%]">
              {link.link_description}
            </span>
          )}
        </div>
      </PrismicNextLink>
    </motion.li>
  )
}
